import { useState } from 'react';
import { api } from '../api/client';
import { useT } from '../lib/i18n';

/**
 * A two-language label editor (pt-PT + English) for admin-defined names — field
 * and section labels, option labels, dashboard-card and section titles. The
 * Portuguese value is the source of truth; English is optional and falls back to
 * it. The "translate" button machine-translates PT→EN via the server (Azure
 * Translator); when the engine is not configured it shows a hint and the admin
 * types English by hand. Editing a label never touches the underlying key/id, so
 * the structure is safe.
 */
export function BilingualLabelInput({
  labelPt,
  labelEn,
  onChange,
  ptPlaceholder,
  ptLocked,
  disabled,
  autoFocus,
}: {
  labelPt: string;
  labelEn?: string | null;
  onChange: (pt: string, en: string) => void;
  ptPlaceholder?: string;
  /** Lock the Portuguese field (e.g. a built-in field's locked label). */
  ptLocked?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState<string | null>(null);

  async function translate() {
    const source = labelPt.trim();
    if (!source || busy) return;
    setBusy(true);
    setHint(null);
    try {
      const res = await api.translate({ texts: [source], to: 'en', from: 'pt' });
      if (!res.configured) {
        setHint(t('bilingual.unconfigured'));
      } else if (res.translations[0]) {
        onChange(labelPt, res.translations[0]);
      }
    } catch {
      setHint(t('bilingual.failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bilingual">
      <div className="bilingual-row">
        <span className="bilingual-tag" title={t('bilingual.pt')}>
          PT
        </span>
        <input
          value={labelPt}
          onChange={(e) => onChange(e.target.value, labelEn ?? '')}
          placeholder={ptPlaceholder}
          disabled={disabled || ptLocked}
          autoFocus={autoFocus}
        />
      </div>
      <div className="bilingual-row">
        <span className="bilingual-tag" title={t('bilingual.en')}>
          EN
        </span>
        <input
          value={labelEn ?? ''}
          onChange={(e) => onChange(labelPt, e.target.value)}
          placeholder={labelPt}
          disabled={disabled}
        />
        <button
          type="button"
          className="secondary bilingual-translate"
          onClick={() => void translate()}
          disabled={disabled || busy || !labelPt.trim()}
          title={t('bilingual.translate')}
        >
          {busy ? t('bilingual.translating') : t('bilingual.translate')}
        </button>
      </div>
      {hint && <small className="bilingual-hint">{hint}</small>}
    </div>
  );
}
